import { InvoiceSummary } from '@finance-hub/domain/invoicing/blocs/types/invoice-summary';

export type InvoiceSummaryAmountSize = '$lg' | '$md' | '$sm' | '$xl' | '$xs';

const AMOUNT_SIZE_LADDER: InvoiceSummaryAmountSize[] = [
  '$xl',
  '$lg',
  '$md',
  '$sm',
  '$xs',
];

const SHRINK_STEPS_BY_LENGTH: { minLength: number; steps: number }[] = [
  { minLength: 12, steps: 4 },
  { minLength: 11, steps: 3 },
  { minLength: 10, steps: 2 },
  { minLength: 8, steps: 1 },
];

export const getLongestInvoiceSummaryAmountLength = (
  summary: InvoiceSummary,
): number =>
  [...summary.amountBuckets, ...summary.timeSeriesBuckets].reduce(
    (longest, bucket) => Math.max(longest, bucket.amount.length),
    0,
  );

/**
 * The amount font size a card should render at.
 *
 * Every card shrinks by the same number of steps — driven by the longest
 * amount in the row — so the row stays visually uniform instead of mixing
 * sizes card to card. Headline and aging cards pass different ceilings
 * because they are different widths.
 *
 * @param longestAmountLength Longest amount length across the whole row.
 * @param ceiling The largest size this card renders at.
 * @returns The size token to pass to the card's amount.
 */
export const getInvoiceSummaryAmountSize = (
  longestAmountLength: number,
  ceiling: InvoiceSummaryAmountSize,
): InvoiceSummaryAmountSize => {
  const steps =
    SHRINK_STEPS_BY_LENGTH.find(
      ({ minLength }) => longestAmountLength >= minLength,
    )?.steps ?? 0;
  const ceilingIndex = AMOUNT_SIZE_LADDER.indexOf(ceiling);

  return (
    AMOUNT_SIZE_LADDER[
      Math.min(ceilingIndex + steps, AMOUNT_SIZE_LADDER.length - 1)
    ] ?? ceiling
  );
};
